import React, { useState } from "react";
import { FaArrowLeft, FaTimes } from "react-icons/fa";
import apiRequest from "../services/api";

const ResetPassword = ({ isOpen, onClose, initialUsername = "" }) => {
  const [step, setStep] = useState("forgot"); // "forgot" or "reset"
  const [forgotEmail, setForgotEmail] = useState(initialUsername);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [resetForm, setResetForm] = useState({
    username: initialUsername,
    token: "",
    newPassword: "",
    confirmPassword: "",
  });

  if (!isOpen) return null;

  const handleClose = () => {
    setError("");
    setSuccessMsg("");
    setStep("forgot");
    onClose();
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!forgotEmail) return setError("Please enter your username/mobile.");

    try {
      setIsSubmitting(true);
      await apiRequest(
        `auth/forgotPassword?username=${encodeURIComponent(forgotEmail)}`,
        { method: "PUT" }
      );
      setResetForm((prev) => ({ ...prev, username: forgotEmail }));
      setSuccessMsg("OTP sent to your registered mobile.");
      setStep("reset");
    } catch (err) {
      setError(err.message || "Unable to send OTP.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!resetForm.token) return setError("Please enter the OTP.");
    if (!resetForm.newPassword) return setError("Please enter a new password.");
    if (resetForm.newPassword !== resetForm.confirmPassword) {
      return setError("Passwords do not match.");
    }

    try {
      setIsSubmitting(true);
      await apiRequest(
        `auth/resetPassword?username=${encodeURIComponent(
          resetForm.username
        )}&token=${encodeURIComponent(
          resetForm.token
        )}&newPassword=${encodeURIComponent(resetForm.newPassword)}`,
        { method: "PUT" }
      );
      setSuccessMsg("Password reset successful! You can now log in.");
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.message || "Unable to reset password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4 backdrop-blur-md">
      <div className="relative bg-white rounded-3xl w-full max-w-md p-6 md:p-8 shadow-2xl border border-sky-100 animate-in fade-in zoom-in duration-200">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2 rounded-full transition-all"
        >
          <FaTimes className="text-lg" />
        </button>

        {/* Feedback Banners */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl border border-red-100">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-sky-50 text-sky-700 text-xs font-bold rounded-xl border border-sky-100">
            {successMsg}
          </div>
        )}

        {/* STEP 1: Request OTP */}
        {step === "forgot" && (
          <div className="animate-in slide-in-from-right duration-300">
            <h2 className="text-xl md:text-2xl font-extrabold text-slate-800 mb-1">
              Account Recovery
            </h2>
            <p className="text-xs text-slate-500 mb-6">
              Enter your registered username or Fayda FAN to receive an OTP.
            </p>

            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] md:text-xs font-bold text-sky-700 uppercase ml-1">
                  Username / Fayda FAN
                </label>
                <input
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full mt-1 bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 focus:bg-white outline-none font-medium text-slate-700 text-sm transition-all"
                  placeholder="Enter Username or Fayda No."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3.5 rounded-xl shadow-lg transition-all active:scale-95"
              >
                {isSubmitting ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
          </div>
        )}

        {/* STEP 2: Reset Password with OTP */}
        {step === "reset" && (
          <div className="animate-in slide-in-from-right duration-300">
            <button
              onClick={() => {
                setError("");
                setSuccessMsg("");
                setStep("forgot");
              }}
              className="flex items-center gap-2 text-slate-400 hover:text-sky-600 mb-3 text-xs font-bold"
            >
              <FaArrowLeft /> Back
            </button>

            <h2 className="text-xl md:text-2xl font-extrabold text-slate-800 mb-1">
              Reset Password
            </h2>
            <p className="text-xs text-slate-500 mb-5">
              Enter the OTP sent to your registered mobile along with your new
              password.
            </p>

            <form onSubmit={handleResetSubmit} className="space-y-3">
              <input
                placeholder="Enter OTP Code"
                value={resetForm.token}
                onChange={(e) =>
                  setResetForm({ ...resetForm, token: e.target.value })
                }
                className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 focus:bg-white outline-none font-medium text-slate-700 text-sm transition-all"
              />

              <input
                type="password"
                placeholder="New Password"
                value={resetForm.newPassword}
                onChange={(e) =>
                  setResetForm({ ...resetForm, newPassword: e.target.value })
                }
                className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 focus:bg-white outline-none font-medium text-slate-700 text-sm transition-all"
              />

              <input
                type="password"
                placeholder="Confirm New Password"
                value={resetForm.confirmPassword}
                onChange={(e) =>
                  setResetForm({
                    ...resetForm,
                    confirmPassword: e.target.value,
                  })
                }
                className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 focus:bg-white outline-none font-medium text-slate-700 text-sm transition-all"
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3.5 rounded-xl shadow-lg transition-all active:scale-95 mt-2"
              >
                {isSubmitting ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
